import { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { getAdminBookings, refundBooking } from '../../lib/api';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Calendar, RotateCcw, ArrowRight, Loader2 } from 'lucide-react';

export default function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  const loadBookings = async (cursor = null, status = statusFilter) => {
    try {
      const params = { limit: 20, cursor };
      if (status) params.status = status;
      
      const response = await getAdminBookings(params);
      
      if (cursor) {
        setBookings(prev => [...prev, ...response.data.items]);
      } else {
        setBookings(response.data.items || []);
      }
      
      setNextCursor(response.data.next_cursor);
      setHasMore(response.data.has_more);
    } catch (error) {
      console.error('Failed to load bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBookings();
  }, []);

  const handleStatusChange = (status) => {
    setStatusFilter(status);
    setLoading(true);
    loadBookings(null, status);
  };

  const handleRefund = async (bookingId, amount) => {
    if (!window.confirm(`Are you sure you want to issue a full refund of $${(amount / 100).toFixed(2)}?`)) return;
    
    setActionLoading(bookingId);
    try {
      await refundBooking(bookingId);
      setBookings(prev => 
        prev.map(b => b.id === bookingId ? { ...b, status: 'refunded' } : b)
      );
      toast.success('Refund issued successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to issue refund');
    } finally {
      setActionLoading(null);
    }
  };

  const formatPrice = (cents) => {
    return (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    });
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'bg-amber-100 text-amber-700',
      approved: 'bg-blue-100 text-blue-700',
      paid: 'bg-green-100 text-green-700',
      completed: 'bg-slate-100 text-slate-700',
      cancelled: 'bg-slate-100 text-slate-500',
      refunded: 'bg-purple-100 text-purple-700'
    };
    return variants[status] || 'bg-slate-100 text-slate-700';
  };

  return (
    <div className="space-y-6" data-testid="admin-bookings">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Bookings</h1>
          <p className="text-slate-500 mt-1">Manage platform bookings and refunds</p>
        </div>
        <Select value={statusFilter} onValueChange={handleStatusChange}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
            <SelectItem value="refunded">Refunded</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : bookings.length > 0 ? (
        <>
          <div className="space-y-4">
            {bookings.map((booking) => (
              <Card key={booking.id}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-slate-900">{booking.property_title}</h3>
                        <Badge className={getStatusBadge(booking.status)}>
                          {booking.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-600">
                        Guest: {booking.guest_name} ({booking.guest_email})
                      </p>
                      <p className="text-sm text-slate-500">
                        {format(new Date(booking.check_in), 'MMM d')} - {format(new Date(booking.check_out), 'MMM d, yyyy')}
                        {' • '}{booking.nights} nights • {booking.guests_count} guests
                      </p>
                      <p className="text-xs text-slate-400">
                        ID: {booking.id}
                      </p>
                    </div>
                    
                    <div className="text-right">
                      <p className="text-lg font-semibold text-slate-900">
                        {formatPrice(booking.total_charged)}
                      </p>
                      <p className="text-sm text-slate-500">
                        Platform fee: {formatPrice(booking.guest_fee_snapshot + booking.host_fee_snapshot)}
                      </p>
                      
                      {booking.status === 'paid' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRefund(booking.id, booking.total_charged)}
                          disabled={actionLoading === booking.id}
                          className="mt-2"
                        >
                          {actionLoading === booking.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <RotateCcw className="w-4 h-4 mr-1" />
                              Issue Refund
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {hasMore && (
            <div className="text-center">
              <Button 
                variant="outline"
                onClick={() => loadBookings(nextCursor)}
              >
                Load More
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}
        </>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
              No bookings found
            </h3>
            <p className="text-slate-500">
              {statusFilter ? `No ${statusFilter} bookings found.` : 'No bookings yet.'}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
